import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { getOrganization, getOrgMembers } from '../services/authService';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './StudyHours.css';

const StudyHours = () => {
  const { orgDomain, isOrgAdminUser, isTeamMember } = useAuth();
  const [members, setMembers] = useState([]);
  const [memberHours, setMemberHours] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timePeriod, setTimePeriod] = useState('week'); // 'week' or 'month'
  const [processing, setProcessing] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // Convert percentage GPA to 4.0 scale
  const convertGPAToFourPointScale = (percentageGPA) => {
    if (percentageGPA == null) return null;
    return percentageGPA / 25; // 100% = 4.0 GPA
  };

  // Get the applicable study goal for a student based on their GPA
  const getApplicableGoal = (studentGPA, studyGoals) => {
    if (!studyGoals) return null;

    // Convert percentage to 4.0 scale
    const gpaOn4Scale = convertGPAToFourPointScale(studentGPA);
    
    if (gpaOn4Scale == null) {
      // No GPA data, use default goal
      return studyGoals.defaultGoal || null;
    }

    // Check if any GPA rules apply
    const applicableRule = studyGoals.gpaRules?.find(rule => {
      return gpaOn4Scale >= rule.minGpa && gpaOn4Scale <= rule.maxGpa;
    });

    if (applicableRule) {
      return { hours: applicableRule.hours, period: applicableRule.period };
    }

    // No rule applies, use default
    return studyGoals.defaultGoal || null;
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!orgDomain) return;

      // Load organization members
      const membersList = await getOrgMembers(orgDomain);
      setMembers(membersList);

      // Calculate date range
      const now = new Date();
      let startDate;
      if (timePeriod === 'week') {
        const dayOfWeek = now.getDay();
        startDate = new Date(now);
        startDate.setDate(now.getDate() - dayOfWeek);
        startDate.setHours(0, 0, 0, 0);
      } else {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      }

      // Load study sessions for the time period
      const sessionsRef = collection(db, 'studySessions');
      const sessionsQuery = query(
        sessionsRef,
        where('organizationDomain', '==', orgDomain),
        where('startTime', '>=', Timestamp.fromDate(startDate)),
        where('startTime', '<=', Timestamp.fromDate(now))
      );

      const sessionsSnapshot = await getDocs(sessionsQuery);
      const hoursMap = {};

      sessionsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.endTime) {
          const userId = data.userId;
          const startTime = data.startTime.toDate();
          const endTime = data.endTime.toDate();
          const durationMs = endTime - startTime;
          const durationHours = durationMs / (1000 * 60 * 60);

          if (!hoursMap[userId]) {
            hoursMap[userId] = 0;
          }
          hoursMap[userId] += durationHours;
        }
      });

      setMemberHours(hoursMap);
    } catch (err) {
      console.error('Error loading study hours:', err);
      setError('Failed to load study hours. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [orgDomain, timePeriod]);

  const generateGoalsReportPDF = async () => {
    try {
      setGeneratingPDF(true);

      // Load organization info and study goals
      const org = await getOrganization(orgDomain);
      if (!org) {
        alert('Organization not found');
        return;
      }

      const studyGoals = org.studyGoals || { defaultGoal: { hours: '', period: 'week' }, gpaRules: [] };
      
      // Check if any goals are set
      if (!studyGoals.defaultGoal?.hours && (!studyGoals.gpaRules || studyGoals.gpaRules.length === 0)) {
        alert('No study goals have been set. Please set goals in the Study Locations page before generating a report.');
        return;
      }

      // Load GPA data for all members
      const memberGoalData = [];
      
      for (const member of members) {
        try {
          // Load courses and calculate GPA
          const userRef = doc(db, 'users', member.userId);
          const coursesRef = collection(userRef, 'courses');
          const coursesSnapshot = await getDocs(coursesRef);
          
          let totalWeightedGrade = 0;
          let totalGradedCourses = 0;

          for (const courseDoc of coursesSnapshot.docs) {
            const assignmentsRef = collection(courseDoc.ref, 'assignments');
            const assignmentsSnapshot = await getDocs(assignmentsRef);
            
            const courseData = courseDoc.data();
            const gradingBreakdown = courseData.gradingBreakdown || {};
            
            const categoryGrades = {};
            const categoryWeights = {};
            
            Object.entries(gradingBreakdown).forEach(([category, weightStr]) => {
              const weight = parseFloat(weightStr.replace('%', '')) || 0;
              categoryWeights[category] = weight;
              categoryGrades[category] = [];
            });

            let hasGradedAssignments = false;
            assignmentsSnapshot.forEach((assignmentDoc) => {
              const assignmentData = assignmentDoc.data();
              if (assignmentData.grade != null && assignmentData.category) {
                hasGradedAssignments = true;
                if (!categoryGrades[assignmentData.category]) {
                  categoryGrades[assignmentData.category] = [];
                }
                categoryGrades[assignmentData.category].push(assignmentData.grade);
              }
            });

            if (hasGradedAssignments) {
              let weightedSum = 0;
              let totalCategoryWeight = 0;
              
              Object.entries(categoryGrades).forEach(([category, grades]) => {
                if (grades.length > 0) {
                  const categoryAverage = grades.reduce((sum, g) => sum + g, 0) / grades.length;
                  const weight = categoryWeights[category] || 0;
                  weightedSum += categoryAverage * (weight / 100);
                  totalCategoryWeight += weight;
                }
              });
              
              if (totalCategoryWeight > 0) {
                const courseGrade = totalCategoryWeight === 100 
                  ? weightedSum 
                  : (weightedSum / totalCategoryWeight) * 100;
                totalWeightedGrade += courseGrade;
                totalGradedCourses++;
              }
            }
          }

          const overallGPA = totalGradedCourses > 0 
            ? totalWeightedGrade / totalGradedCourses 
            : null;

          const applicableGoal = getApplicableGoal(overallGPA, studyGoals);
          const actualHours = memberHours[member.userId] || 0;
          
          // Convert goal period to match current timePeriod
          let requiredHours = 0;
          if (applicableGoal && applicableGoal.hours) {
            if (applicableGoal.period === timePeriod) {
              requiredHours = parseFloat(applicableGoal.hours);
            } else if (applicableGoal.period === 'month' && timePeriod === 'week') {
              requiredHours = parseFloat(applicableGoal.hours) / 4; // Approximate weekly from monthly
            } else if (applicableGoal.period === 'week' && timePeriod === 'month') {
              requiredHours = parseFloat(applicableGoal.hours) * 4; // Approximate monthly from weekly
            }
          }

          const meetingGoal = requiredHours > 0 && actualHours >= requiredHours;
          const hasGoal = requiredHours > 0;

          memberGoalData.push({
            name: member.name,
            email: member.email,
            gpa: overallGPA,
            gpaOn4Scale: convertGPAToFourPointScale(overallGPA),
            actualHours,
            requiredHours,
            meetingGoal,
            hasGoal,
            goalPeriod: applicableGoal?.period || null,
          });
        } catch (err) {
          console.error(`Error loading data for ${member.email}:`, err);
          memberGoalData.push({
            name: member.name,
            email: member.email,
            gpa: null,
            gpaOn4Scale: null,
            actualHours: memberHours[member.userId] || 0,
            requiredHours: 0,
            meetingGoal: false,
            hasGoal: false,
            goalPeriod: null,
          });
        }
      }

      // Sort: Meeting goals first, then by hours (descending)
      memberGoalData.sort((a, b) => {
        if (a.meetingGoal !== b.meetingGoal) {
          return b.meetingGoal ? 1 : -1;
        }
        return b.actualHours - a.actualHours;
      });

      // Generate PDF
      const pdfContainer = document.createElement("div");
      pdfContainer.style.position = "absolute";
      pdfContainer.style.left = "-9999px";
      pdfContainer.style.width = "210mm";
      pdfContainer.style.padding = "18mm";
      pdfContainer.style.backgroundColor = "#ffffff";
      pdfContainer.style.fontFamily = "Arial, sans-serif";
      pdfContainer.style.color = "#1a1a1a";
      document.body.appendChild(pdfContainer);

      const logoPath = window.location.origin + "/textLogo.png";
      const meetingGoalCount = memberGoalData.filter(m => m.meetingGoal && m.hasGoal).length;
      const notMeetingGoalCount = memberGoalData.filter(m => !m.meetingGoal && m.hasGoal).length;
      const noGoalCount = memberGoalData.filter(m => !m.hasGoal).length;
      const totalWithGoals = memberGoalData.filter(m => m.hasGoal).length;
      const complianceRate = totalWithGoals > 0 ? Math.round((meetingGoalCount / totalWithGoals) * 100) : 0;

      pdfContainer.innerHTML = `
        <div style="display: flex; flex-direction: column; gap: 16px;">
          <!-- Header -->
          <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #238a94; padding-bottom: 12px;">
            <div>
              <img src="${logoPath}" alt="SyllySync" style="height: 40px; object-fit: contain; max-width: 160px;" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';">
              <div style="display: none; font-size: 20px; font-weight: 700; color: #238a94;">SyllySync</div>
            </div>
            <div style="text-align: right; color: #666; font-size: 11px;">
              <div style="font-weight: 600;">Study Goals Compliance Report</div>
              <div style="margin-top: 3px;">${org.name || orgDomain}</div>
              <div style="margin-top: 2px;">${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
            </div>
          </div>

          <!-- Title -->
          <div style="text-align: center; padding: 10px 0;">
            <h1 style="margin: 0; font-size: 24px; color: #238a94; font-weight: 700;">Study Hour Goals Report</h1>
            <div style="margin-top: 4px; font-size: 13px; color: #666;">
              ${timePeriod === 'week' ? 'Current Week' : 'Current Month'} Performance
            </div>
          </div>

          <!-- Summary Stats -->
          <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 8px;">
            <div style="background: linear-gradient(135deg, #4caf50 0%, #45a049 100%); padding: 14px; border-radius: 8px; color: white; text-align: center;">
              <div style="font-size: 10px; opacity: 0.9; margin-bottom: 4px;">Meeting Goals</div>
              <div style="font-size: 28px; font-weight: 700; line-height: 1;">${meetingGoalCount}</div>
              <div style="font-size: 9px; opacity: 0.8; margin-top: 3px;">${totalWithGoals > 0 ? Math.round((meetingGoalCount/totalWithGoals)*100) : 0}% compliant</div>
            </div>
            <div style="background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); padding: 14px; border-radius: 8px; color: white; text-align: center;">
              <div style="font-size: 10px; opacity: 0.9; margin-bottom: 4px;">Not Meeting Goals</div>
              <div style="font-size: 28px; font-weight: 700; line-height: 1;">${notMeetingGoalCount}</div>
              <div style="font-size: 9px; opacity: 0.8; margin-top: 3px;">${totalWithGoals > 0 ? Math.round((notMeetingGoalCount/totalWithGoals)*100) : 0}% non-compliant</div>
            </div>
            <div style="background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%); padding: 14px; border-radius: 8px; color: white; text-align: center;">
              <div style="font-size: 10px; opacity: 0.9; margin-bottom: 4px;">No Goal Set</div>
              <div style="font-size: 28px; font-weight: 700; line-height: 1;">${noGoalCount}</div>
              <div style="font-size: 9px; opacity: 0.8; margin-top: 3px;">No GPA data</div>
            </div>
            <div style="background: linear-gradient(135deg, #238a94 0%, #1d6f77 100%); padding: 14px; border-radius: 8px; color: white; text-align: center;">
              <div style="font-size: 10px; opacity: 0.9; margin-bottom: 4px;">Compliance Rate</div>
              <div style="font-size: 28px; font-weight: 700; line-height: 1;">${complianceRate}%</div>
              <div style="font-size: 9px; opacity: 0.8; margin-top: 3px;">Of students w/ goals</div>
            </div>
          </div>

          <!-- Student Details Table -->
          <div style="margin-top: 8px;">
            <h2 style="margin: 0 0 10px 0; font-size: 14px; color: #1f2937; font-weight: 600;">Student Performance Details</h2>
            <table style="width: 100%; border-collapse: collapse; font-size: 10px;">
              <thead>
                <tr style="background: #f3f4f6; border-bottom: 2px solid #e5e7eb;">
                  <th style="padding: 8px 6px; text-align: left; font-weight: 600; color: #374151;">Student</th>
                  <th style="padding: 8px 6px; text-align: center; font-weight: 600; color: #374151; width: 70px;">GPA (4.0)</th>
                  <th style="padding: 8px 6px; text-align: center; font-weight: 600; color: #374151; width: 70px;">Required</th>
                  <th style="padding: 8px 6px; text-align: center; font-weight: 600; color: #374151; width: 70px;">Actual</th>
                  <th style="padding: 8px 6px; text-align: center; font-weight: 600; color: #374151; width: 60px;">Status</th>
                </tr>
              </thead>
              <tbody>
                ${memberGoalData.map((student, index) => `
                  <tr style="border-bottom: 1px solid #e5e7eb; ${index % 2 === 0 ? 'background: #fafafa;' : ''}">
                    <td style="padding: 8px 6px;">
                      <div style="font-weight: 500; color: #1f2937;">${student.name}</div>
                      <div style="font-size: 8px; color: #6b7280; margin-top: 2px;">${student.email}</div>
                    </td>
                    <td style="padding: 8px 6px; text-align: center; color: #4b5563;">
                      ${student.gpaOn4Scale != null ? student.gpaOn4Scale.toFixed(2) : 'N/A'}
                      ${student.gpa != null ? `<div style="font-size: 8px; color: #9ca3af;">(${student.gpa.toFixed(1)}%)</div>` : ''}
                    </td>
                    <td style="padding: 8px 6px; text-align: center; color: #4b5563; font-weight: 500;">
                      ${student.hasGoal ? `${student.requiredHours.toFixed(1)} hrs` : '-'}
                    </td>
                    <td style="padding: 8px 6px; text-align: center; color: #1f2937; font-weight: 600;">
                      ${student.actualHours.toFixed(1)} hrs
                    </td>
                    <td style="padding: 8px 6px; text-align: center;">
                      ${student.hasGoal 
                        ? (student.meetingGoal 
                          ? '<span style="color: #10b981; font-weight: 600; font-size: 9px;">✓ Met</span>' 
                          : '<span style="color: #ef4444; font-weight: 600; font-size: 9px;">✗ Not Met</span>')
                        : '<span style="color: #9ca3af; font-size: 9px;">No Goal</span>'
                      }
                    </td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>

          <!-- Footer -->
          <div style="margin-top: 12px; padding-top: 8px; border-top: 1px solid #e5e7eb; font-size: 9px; color: #9ca3af; text-align: center;">
            <div>Generated by SyllySync Admin Portal • ${new Date().toLocaleString('en-US')}</div>
            <div style="margin-top: 3px;">For ${timePeriod === 'week' ? 'current week' : 'current month'} study hours</div>
          </div>
        </div>
      `;

      // Wait for images to load
      await new Promise(resolve => setTimeout(resolve, 500));

      // Generate PDF using html2canvas
      const canvas = await html2canvas(pdfContainer, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/png');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      
      // Save PDF
      const fileName = `study-goals-report-${orgDomain}-${timePeriod}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

      // Clean up
      document.body.removeChild(pdfContainer);
      
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Failed to generate PDF report. Please try again.');
    } finally {
      setGeneratingPDF(false);
    }
  };

  useEffect(() => {
    if (orgDomain && (isOrgAdminUser || isTeamMember)) {
      loadData();
    }
  }, [orgDomain, isOrgAdminUser, isTeamMember, timePeriod, loadData]);

  // Check if user is admin or team member
  if (!isOrgAdminUser && !isTeamMember) {
    return (
      <div className="study-hours-page">
        <div className="page-header">
          <h1>Study Hours</h1>
        </div>
        <div className="error-state">
          <p>You must be an admin or team member to view study hours.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="study-hours-page">
        <div className="page-header">
          <h1>Study Hours</h1>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading study hours...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="study-hours-page">
        <div className="page-header">
          <h1>Study Hours</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={loadData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Sort members by hours (descending)
  const membersWithHours = members
    .map((member) => ({
      ...member,
      hours: memberHours[member.userId] || 0,
    }))
    .sort((a, b) => b.hours - a.hours);

  const totalHours = Object.values(memberHours).reduce((sum, hours) => sum + hours, 0);
  const activeStudents = Object.keys(memberHours).length;

  return (
    <div className="study-hours-page">
      <div className="page-header">
        <h1>Study Hours</h1>
        {orgDomain && <p className="org-domain">Domain: {orgDomain}</p>}
        <div className="header-controls">
          <select
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value)}
            className="period-select"
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
          <button 
            onClick={generateGoalsReportPDF} 
            className="generate-pdf-button" 
            disabled={generatingPDF || processing}
          >
            {generatingPDF ? '⏳ Generating...' : '📄 Goals Report'}
          </button>
          <button onClick={loadData} className="refresh-button" disabled={processing}>
            🔄 Refresh
          </button>
        </div>
      </div>

      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-value">{totalHours.toFixed(1)}</div>
          <div className="stat-label">Total Hours</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{activeStudents}</div>
          <div className="stat-label">Active Students</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {activeStudents > 0 ? (totalHours / activeStudents).toFixed(1) : '0.0'}
          </div>
          <div className="stat-label">Avg Hours per Student</div>
        </div>
      </div>

      {membersWithHours.length === 0 ? (
        <div className="empty-state">
          <p>No members found.</p>
        </div>
      ) : (
        <div className="hours-table-container">
          <table className="hours-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th className="hours-column">
                  Hours ({timePeriod === 'week' ? 'This Week' : 'This Month'})
                </th>
              </tr>
            </thead>
            <tbody>
              {membersWithHours.map((member) => (
                <tr key={member.userId}>
                  <td>
                    <div className="member-cell">
                      <div className="member-avatar">
                        {member.photoURL && (
                          <img 
                            src={member.photoURL} 
                            alt={member.name}
                            className="avatar-image"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallback = e.target.nextElementSibling;
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                        )}
                        <span className="avatar-fallback" style={{ display: member.photoURL ? 'none' : 'flex' }}>
                          {member.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      {member.name}
                    </div>
                  </td>
                  <td>{member.email}</td>
                  <td>
                    {member.isAdmin && (
                      <span className="role-badge admin-badge">Admin</span>
                    )}
                    {member.isTeamMember && !member.isAdmin && (
                      <span className="role-badge team-badge">Team</span>
                    )}
                    {!member.isAdmin && !member.isTeamMember && (
                      <span className="role-badge member-badge">Member</span>
                    )}
                  </td>
                  <td className="hours-column">
                    <div className="hours-display">
                      <span className="hours-value">{member.hours.toFixed(1)}</span>
                      <span className="hours-unit">hrs</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StudyHours;
